import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useUserWebSocket } from '../hooks/useUserWebSocket';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const handleUserUpdate = useCallback((freshUser) => {
    setUser(freshUser);
    localStorage.setItem('phantomtrades_user', JSON.stringify(freshUser));
  }, []);

  const handleLogoutFromWS = useCallback(() => {
    setUser(null);
    localStorage.removeItem('phantomtrades_user');
    localStorage.removeItem('phantomtrades_token');
    // Hard-redirect so any guarded UI unmounts cleanly
    window.location.href = '/login';
  }, []);

  // Open WebSocket once authenticated
  const token = localStorage.getItem('phantomtrades_token');
  useUserWebSocket({
    userId: user?.id || user?.user_id,
    token,
    onUserUpdate: handleUserUpdate,
    onLogout: handleLogoutFromWS,
  });

  useEffect(() => {
    // Check if user is logged in (from localStorage)
    const storedUser = localStorage.getItem('phantomtrades_user');
    const storedToken = localStorage.getItem('phantomtrades_token');
    
    if (storedUser && storedToken) {
      try {
        const parsedUser = JSON.parse(storedUser);
        setUser(parsedUser);
        
        // Refresh user data from API to get latest balance
        const refreshUserData = async () => {
          try {
            const { default: api } = await import('../services/api');
            const response = await api.get('/auth/me', {
              headers: { Authorization: `Bearer ${storedToken}` }
            });
            
            // Backend returns user directly, not wrapped in {user: ...}
            if (response.data) {
              const freshUser = response.data.user || response.data;
              setUser(freshUser);
              localStorage.setItem('phantomtrades_user', JSON.stringify(freshUser));
            }
          } catch (error) {
            console.error('Error refreshing user data:', error);
            // If token is invalid, clear auth
            if (error.response?.status === 401) {
              localStorage.removeItem('phantomtrades_user');
              localStorage.removeItem('phantomtrades_token');
              setUser(null);
            }
          }
        };
        
        refreshUserData();
      } catch (error) {
        console.error('Error parsing stored user:', error);
        localStorage.removeItem('phantomtrades_user');
        localStorage.removeItem('phantomtrades_token');
      }
    }
    setLoading(false);
  }, []);

  const login = (userData) => {
    // Accept real user data from API instead of mock data
    if (userData && typeof userData === 'object') {
      setUser(userData);
      localStorage.setItem('phantomtrades_user', JSON.stringify(userData));
      localStorage.setItem('phantomtrades_token', userData.token || '');
      return Promise.resolve(userData);
    }
    return Promise.reject(new Error('Invalid user data'));
  };

  const signup = (userData) => {
    // Accept real user data from API instead of mock data
    if (userData && typeof userData === 'object') {
      setUser(userData);
      localStorage.setItem('phantomtrades_user', JSON.stringify(userData));
      localStorage.setItem('phantomtrades_token', userData.token || '');
      return Promise.resolve(userData);
    }
    return Promise.reject(new Error('Invalid user data'));
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('phantomtrades_user');
    localStorage.removeItem('phantomtrades_token');
  };

  const value = {
    user,
    login,
    signup,
    logout,
    loading,
    updateUser: (patch) => handleUserUpdate({ ...user, ...patch }),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
